import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import UspItem from './usp-item';

import { Clock, Cube } from '@phosphor-icons/react';
import ExtendableGroupAuto from '../../components/extendable-group/extendable-group-auto';
import { asHtml } from '../../helpers/markdown';
import { getItemInformation } from '../../selectors/item';
import Content from '../../styled-components/components/Content';
import { HStack } from '../../styled-components/components/HStack';
import { StatusBadge } from '../../styled-components/components/ProductCard/wrapper';
import { Text } from '../../styled-components/components/Text';
import { getConvertedMileage, getMarketOdometer } from '../../utils/market';

const preBlurred = (s: string) => s.replaceAll('******', '\\_\\_SANITIZED\\_\\_');
const blurred = (s: string) =>
  s.replaceAll('__SANITIZED__', '<span class="hidden-information">******</span>');

const Information = () => {
  const { t } = useTranslation(['common', 'glossary']);
  const item = useSelector(getItemInformation);
  const contentRef = useRef(null);

  if (!item) return null;

  const {
    title,
    shortDescription,
    description,
    fuelTypes,
    gearboxType,
    modelYear,
    isDemoVersion,
    mileage,
    odometerReading,
  } = item;

  const odometer = odometerReading
    ? getMarketOdometer(odometerReading)
    : getConvertedMileage(mileage);

  const expectedDeliveryUnit = (unit: string, count: number) => {
    switch (unit) {
      case 'day':
        return t('deliveryTimeRange.unitDay', { ns: 'item', count });
      case 'week':
        return t('deliveryTimeRange.unitWeek', { ns: 'item', count });
      case 'month':
        return t('deliveryTimeRange.unitMonth', { ns: 'item', count });
      default:
        return '';
    }
  };

  return (
    <div className="item-section">
      <div className="m-b">
        <HStack $spacing={1.25} $valign="center">
          {item?.inventoryStatusString === 'InStock' && (
            <StatusBadge $tone="positive">
              {t('inStock', { ns: 'common' })}
              <Cube size={16} weight="bold" />
            </StatusBadge>
          )}
          {item?.inventoryStatusString === 'Incoming' && (
            <>
              <StatusBadge $tone="neutral">
                {t('incoming', { ns: 'common' })}
                <Clock size={16} weight="bold" />
              </StatusBadge>
              {item.availableFrom && (
                <Text $tone="alt" $size="small">
                  {t('availableFromDate', {
                    ns: 'item',
                    date: format(new Date(item.availableFrom), 'dd MMM yyyy', {
                      locale: sv,
                    }),
                  })}
                </Text>
              )}
            </>
          )}
          {item?.inventoryStatusString === 'Order' && (
            <>
              <StatusBadge $tone="neutral">
                {t('forOrder', { ns: 'common' })}
                <Clock size={16} weight="bold" />
              </StatusBadge>
              {item.expectedDelivery && (
                <Text $tone="alt" $size="small">
                  {t('deliveryTimeRange.label', {
                    ns: 'item',
                    from: item.expectedDelivery.from,
                    to: item.expectedDelivery.to,
                    unit: expectedDeliveryUnit(
                      item.expectedDelivery.unit,
                      item.expectedDelivery.to,
                    ),
                  })}
                </Text>
              )}
            </>
          )}
        </HStack>
      </div>
      <h1 className="h2 hyphens-auto">
        {title} <span className="font-light">{shortDescription}</span>
      </h1>

      <div className="m-t">
        <div data-wx-uspgrid="">
          <UspItem title={fuelTypes} icon="fuel" />
          <UspItem title={gearboxType} icon="gearbox" />
          <UspItem title={odometer} icon="mileage" />
          <UspItem title={modelYear} icon="calendar" isDemoVersion={isDemoVersion} />
        </div>
      </div>
      {description && (
        <div className="m-t">
          <ExtendableGroupAuto label={t('readMore', { ns: 'common' })} childRef={contentRef}>
            <div ref={contentRef}>
              <Content
                dangerouslySetInnerHTML={{ __html: blurred(asHtml(preBlurred(description))) }}
              />
            </div>
          </ExtendableGroupAuto>
        </div>
      )}
    </div>
  );
};

export default Information;
